<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title
        >友だち追加経路別初期チャットメッセージ 新規作成</template
      >
      <template #right>
        <FlatButton @click="handleSubmit" :disabled="isSubmitDisabled">
          保存
        </FlatButton>
      </template>
    </SettingsPagePanelHeader>

    <div :class="$style.introductionMessageForm">
      <IntroductionMessageConditionForm
        v-if="inflowSourceOptions"
        v-model="conditionForm"
        :inflowSourceOptions="inflowSourceOptions"
      />

      <IntroductionMessageContentForm
        v-if="
          scenarioChatOptions &&
          pageQuery.result.value?.viewer.hasLineActivationProvider != null
        "
        v-model="contentForm"
        :class="$style.introductionMessageForm__messageContent"
        :scenarioChatOptions="scenarioChatOptions"
        :hasLineActivationProvider="
          pageQuery.result.value?.viewer.hasLineActivationProvider
        "
      />
    </div>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import { captureException as newrelicCaptureException } from '@/newrelic'
import type { CreateConditionalIntroductionMessageInput } from '@/types/schema.generated'

import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useCreateConditionalIntroductionMessageMutation,
  useSettingsPageIntroductionMessageConditionalCreateViewQuery,
  type CreateConditionalIntroductionMessageMutation,
} from './SettingsPageIntroductionMessageConditionalCreateView.generated'
import IntroductionMessageConditionForm from './IntroductionMessageConditionForm.vue'
import IntroductionMessageContentForm from './IntroductionMessageContentForm.vue'
import {
  useFetchInflowSourceConditionQuery,
  useFetchScenarioChatsQuery,
} from './IntroductionMessageForm.generated'
import {
  type IntroductionMessageContentFormType,
  type IntroductionMessageConditionFormType,
  validateIntroductionMessageConditionForm,
  validateIntroductionMessageContentForm,
} from './formSchema'
import { useLogger } from '@/logger/logger'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const alert = useAlert()
const logger = useLogger()
const router = useRouter()

const pageQuery = useSettingsPageIntroductionMessageConditionalCreateViewQuery()
const { result: inflowSourceResult } = useFetchInflowSourceConditionQuery()

const inflowSourceOptions = computed(() => {
  return inflowSourceResult.value?.segmentConditionParameters.inflowSourceCondition.inflowSources.map(
    source => ({
      text: source.inflowSourceName,
      value: source.guid,
    })
  )
})

const { result: scenarioChatResult } = useFetchScenarioChatsQuery()

const scenarioChatOptions = computed(() => {
  return scenarioChatResult.value?.scenarioChats.scenarioChats.map(chat => ({
    text: chat.title,
    value: chat.guid,
  }))
})

const initialConditionFormValue = {
  inflowSourceId: '',
}

const conditionForm = ref<IntroductionMessageConditionFormType>(
  initialConditionFormValue
)

const initialContentFormValue = {
  // type: 'scenarioChat' as const,
  // scenarioChatGuid: '',
  type: 'plain' as const,
  body: '',
  selections: [],
}

const contentForm = ref<IntroductionMessageContentFormType>(
  initialContentFormValue
)

const isEdited = computed(
  () =>
    JSON.stringify(conditionForm.value) !==
      JSON.stringify(initialConditionFormValue) ||
    JSON.stringify(contentForm.value) !==
      JSON.stringify(initialContentFormValue)
)

const isSubmitting = ref(false)
const isSubmitDisabled = computed(() => {
  // 送信中もボタンを disable する
  return (
    !!validateIntroductionMessageConditionForm(conditionForm.value) ||
    !!validateIntroductionMessageContentForm(contentForm.value) ||
    isSubmitting.value
  )
})

const { mutate: createConditionalIntroductionMessageMutation } =
  useCreateConditionalIntroductionMessageMutation()

const handleSubmit = async () => {
  isSubmitting.value = true
  const input: CreateConditionalIntroductionMessageInput = {
    condition: {
      userRegistrationInflowSourceDefinitionGUID:
        conditionForm.value.inflowSourceId,
    },
    content:
      contentForm.value.type === 'scenarioChat'
        ? {
            scenarioChatIntroductionMessage: {
              scenarioChatGuid: contentForm.value.scenarioChatGuid,
            },
          }
        : {
            plainIntroductionMessage: {
              body: contentForm.value.body,
              selections: contentForm.value.selections,
            },
          },
  }

  try {
    logger.ltlPeople.event.createConditionalIntroductionMessage(
      logger.buildMeta(),
      {
        data: {
          condition: {
            userRegistrationInflowSourceDefinitionGUID:
              conditionForm.value.inflowSourceId,
          },
          content:
            contentForm.value.type === 'scenarioChat'
              ? {
                  scenarioChatGuid: contentForm.value.scenarioChatGuid,
                }
              : {
                  body: contentForm.value.body,
                  selections: contentForm.value.selections,
                },
        },
      }
    )

    const payload = await createConditionalIntroductionMessageMutation({
      input: input,
    })

    if (payload?.errors || !payload?.data) {
      throw new Error('createConditionalIntroductionMessageMutation failed')
    }

    defineResponseHandlers<
      CreateConditionalIntroductionMessageMutation['createConditionalIntroductionMessage'],
      void
    >({
      AlreadyExistsConditionalIntroductionMessageError: () => {
        isSubmitting.value = false
        alert.error('この友だち追加経路は既に使用されています。')
      },
      SaveIntroductionMessageSuccess: () => {
        isSubmitting.value = false
        // eslint-disable-next-line fp/no-mutating-methods
        router.push({ name: 'introductionMessageSetting' })
        alert.success('友だち追加経路別初期チャットメッセージを作成しました。')
      },
    })(payload.data.createConditionalIntroductionMessage)
  } catch (error) {
    newrelicCaptureException(error)
    alert.error('エラーが発生しました。')
    isSubmitting.value = false
  }
}

onBeforeRouteLeave((_to, _from, next) => {
  if (!isEdited.value || !isSubmitting.value) {
    next()
    return
  }

  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})
</script>

<style lang="scss" module>
.introductionMessageForm {
  &__messageContent {
    margin-top: 24px;
  }
}
</style>
